import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, RefreshCw, HeartPulse, Sunrise, Compass } from "lucide-react";
import { Logo } from "@/components/layout/Logo";
import { Eyebrow } from "@/components/fionova/Primitives";
import { listSnapshots, reflect } from "@/services/unsaidService";

const META = {
  xray: { title: "Emotional X-Ray", eyebrow: "Emotional X-Ray", Icon: HeartPulse, reflectLabel: "New emotional check-in", loading: "Looking a little deeper…" },
  plan: { title: "Daily Battle Plan", eyebrow: "Daily Battle Plan", Icon: Sunrise, reflectLabel: "Build today's plan", loading: "Making today a little lighter…" },
  purpose: { title: "Purpose", eyebrow: "Purpose", Icon: Compass, reflectLabel: "Reflect on purpose again", loading: "Sitting with everything you've shared…" },
};

const fmt = (iso) => {
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return ""; }
};

const changeKey = (kind, d = {}) =>
  kind === "xray" ? d.primary_state?.label : kind === "plan" ? d.focus : d.statement;

const Field = ({ label, title, children, emphasized }) => (
  <div className={`rounded-2xl p-4 ring-1 ring-border ${emphasized ? "bg-primary/[0.06]" : "bg-background"}`}>
    <p className={`eyebrow text-[11px] ${emphasized ? "text-primary" : "text-muted-foreground"}`}>{label}</p>
    {title && <p className="mt-1 text-base font-medium leading-snug text-foreground">{title}</p>}
    {children && <p className="mt-1 text-sm leading-relaxed text-foreground/85">{children}</p>}
  </div>
);

const SnapshotBody = ({ kind, d }) => {
  if (kind === "xray") {
    const underneath = d.underneath && d.underneath.detail ? d.underneath : null;
    return (
      <div className="space-y-2.5">
        <Field label="Right now" title={d.primary_state?.label} emphasized>{d.primary_state?.detail}</Field>
        {d.driver?.detail && <Field label="What may be driving it" title={d.driver.label}>{d.driver.detail}</Field>}
        {underneath && <Field label="What may be underneath" title={underneath.label}>{underneath.detail}</Field>}
        {d.need?.detail && <Field label="What you may need" title={d.need.label}>{d.need.detail}</Field>}
        {d.eq_opportunity?.detail && <Field label="Your EQ opportunity" title={d.eq_opportunity.label} emphasized>{d.eq_opportunity.detail}</Field>}
      </div>
    );
  }
  if (kind === "plan") {
    const watch = d.watch_out && d.watch_out.detail ? d.watch_out : null;
    return (
      <div className="space-y-2.5">
        <Field label="Today's focus" emphasized>{d.focus}</Field>
        {(d.priorities || []).map((p, i) => (
          <Field key={i} label={`Step ${i + 1}`} title={p.action}>{p.why}</Field>
        ))}
        {watch && <Field label="Something to watch for" title={watch.label}>{watch.detail}</Field>}
      </div>
    );
  }
  // purpose
  return (
    <div className="space-y-2.5">
      <Field label="What your purpose may point toward" emphasized>{d.statement}</Field>
      {d.surfacing && <Field label="What keeps surfacing">{d.surfacing}</Field>}
      {d.pattern && <Field label="The pattern">{d.pattern}</Field>}
      {d.strengths?.length > 0 && (
        <div className="rounded-2xl bg-background p-4 ring-1 ring-border">
          <p className="eyebrow text-[11px] text-muted-foreground">Strengths</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {d.strengths.map((s, i) => (
              <span key={i} className="rounded-full bg-card px-3 py-1 text-sm text-foreground ring-1 ring-border">{s}</span>
            ))}
          </div>
        </div>
      )}
      {d.purpose_step?.action && <Field label="Your purpose step" emphasized>{d.purpose_step.action}</Field>}
    </div>
  );
};

export default function JourneyHistory() {
  const { kind } = useParams();
  const navigate = useNavigate();
  const meta = META[kind];
  const [snaps, setSnaps] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    if (!meta) return;
    started.current = false;
    setLoaded(false); setSnaps([]);
    listSnapshots(kind).then((s) => { setSnaps(s); setLoaded(true); }).catch(() => setLoaded(true));
  }, [kind, meta]);

  if (!meta) { navigate("/app", { replace: true }); return null; }

  const doReflect = async () => {
    if (busy) return;
    setBusy(true); setError(false);
    try {
      const snap = await reflect(kind);
      setSnaps((prev) => [snap, ...prev]);
    } catch { setError(true); }
    setBusy(false);
  };

  const { Icon } = meta;
  const changed = snaps.length >= 2 && changeKey(kind, snaps[0].data) &&
    changeKey(kind, snaps[0].data) !== changeKey(kind, snaps[1].data);

  return (
    <div data-testid={`page-history-${kind}`} className="fio-immersive min-h-screen w-full"><div className="fio-ambient" aria-hidden><span /><i /></div>
      <div className="mx-auto flex min-h-screen w-full max-w-[600px] flex-col px-6 py-8 sm:px-8">
        <div className="fio-appbar sticky top-3 z-20 flex items-center justify-between rounded-full py-1.5 pl-4 pr-1.5" data-testid="app-header">
          <button data-testid="history-back" onClick={() => navigate("/app")}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Home
          </button>
          <Logo size={36} />
        </div>

        <div className="mt-8">
          <div className="flex items-center gap-2 text-primary">
            <Icon className="h-4 w-4" strokeWidth={1.5} />
            <Eyebrow>{meta.eyebrow}</Eyebrow>
          </div>
          <h1 className="mt-2 text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl">
            {kind === "xray" && "How things have shifted"}
            {kind === "plan" && "Your battle plans over time"}
            {kind === "purpose" && "How your purpose is evolving"}
          </h1>
        </div>

        <button data-testid="history-reflect" onClick={doReflect} disabled={busy}
          className="mt-6 flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-base font-semibold text-primary-foreground transition-all hover:bg-primary/90 disabled:opacity-50">
          <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />
          {busy ? meta.loading : meta.reflectLabel}
        </button>
        {error && <p data-testid="history-error" className="mt-2 text-center text-sm text-muted-foreground">I couldn't gather my thoughts just now. Try again in a moment.</p>}

        {loaded && snaps.length === 0 && !busy && (
          <div data-testid="history-empty" className="mt-6 rounded-3xl bg-card p-6 text-center ring-1 ring-border">
            <p className="text-base leading-relaxed text-muted-foreground">
              Nothing here yet. Take a check-in above and it will be saved so you can look back and see how things change over time.
            </p>
          </div>
        )}

        {loaded && snaps.length === 1 && (
          <p data-testid="history-firstnote" className="mt-6 text-sm leading-relaxed text-muted-foreground">
            This is where your history lives. As you keep checking in, each one is saved here so you can see how things shift — nothing is ever overwritten.
          </p>
        )}

        {changed && (
          <div data-testid="history-changed" className="mt-6 rounded-3xl bg-primary/[0.06] p-5 ring-1 ring-primary/30">
            <p className="eyebrow text-[11px] text-primary">What's changed</p>
            <p className="mt-1.5 text-sm leading-relaxed text-foreground/90">
              Last time: <span className="text-foreground">“{changeKey(kind, snaps[1].data)}”</span>. Now:{" "}
              <span className="text-foreground">“{changeKey(kind, snaps[0].data)}”</span>.
            </p>
          </div>
        )}

        <div className="mt-6 space-y-4 pb-10">
          {snaps.map((s, i) => (
            <motion.div key={s.id} data-testid="history-snapshot"
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
              className="rounded-3xl bg-card p-5 ring-1 ring-border">
              <div className="mb-3 flex items-center justify-between">
                <p className="eyebrow text-[11px] text-muted-foreground">{fmt(s.created_at)}</p>
                {i === 0 && <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-medium text-primary">Latest</span>}
              </div>
              <SnapshotBody kind={kind} d={s.data} />
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
